import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useModal } from '@/contexts/modal'
import { useTourHandler } from '@/contexts/tour-handler'
import useDataGridRefetch from '@/hooks/useDataGridRefetch'
import { useFormPersistence } from '@/hooks/useFormPersistence'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { CreateCompanyProps } from '@/modals/CompanyModals/CreateCompany'
import { getCompaniesOptions } from '@/modules/Company/services'
import {
    checkCustomerEmailExists,
    checkCustomerPhoneExists,
    createCustomer,
    CustomerEmailExistsResponse,
    CustomerPhoneExistsResponse,
    scanBusinessCards,
} from '@/modules/Customer/services'
import type {
    CardScanResponse,
    CustomerCreateFormValues,
} from '@/modules/Customer/types'
import useAuthStore from '@/stores/useAuthStore'
import { paths } from '@/utils/path'
import { yupResolver } from '@hookform/resolvers/yup'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { createNewCustomerSchema } from '../components/New/schema'
import { useCustomerCreateTour } from './use-customer-create-tour'

/**
 * Business logic hook for Customer Create form
 * Manages form state, validation, email checking, and submission
 */
export const useCustomerCreate = () => {
    const [currentStep, setCurrentStep] = useState(1)
    const [emailChecking, setEmailChecking] = useState(false)
    const [emailValid, setEmailValid] = useState(false)
    const [emailExistsResult, setEmailExistsResult] =
        useState<CustomerEmailExistsResponse | null>(null)

    // Realtime check số điện thoại: chỉ chặn khi trùng Lead Marketing (contact_sources)
    const [phoneChecking, setPhoneChecking] = useState(false)
    const [phoneMatchesLead, setPhoneMatchesLead] = useState(false)
    const [phoneExistsResult, setPhoneExistsResult] =
        useState<CustomerPhoneExistsResponse | null>(null)

    const [isScanning, setIsScanning] = useState(false)
    const scanInputRef = useRef<HTMLInputElement>(null)

    const { employee } = useAuthStore()
    const navigate = useNavigate()
    const location = useLocation()
    const customerNameFromBusiness = location.state?.customer_name || ''
    const { t } = useTranslation('customer')
    const { setModal, closeModal } = useModal()

    // Refetch callbacks
    const { refetchCurrent } = useDataGridRefetch({
        reactQueryKey: 'customer-qualified-prospects',
    })

    const { refetchCurrent: refetchCompany } = useDataGridRefetch({
        reactQueryKey: 'company.company_id',
    })

    // Default form values
    const defaultValues: CustomerCreateFormValues = useMemo(
        () => ({
            customer: {
                customer_type: 'company',
                customer_name: customerNameFromBusiness,
                customer_email: '',
                customer_note: '',
                customer_status: '',
                customer_industry: '',
                customer_phone: '',
                customer_position: '',
                customer_source: 'research',
                customer_service_interestings: [],
                customer_sale_followers: employee?.employee_id
                    ? [employee.employee_id]
                    : [],
                customer_contact_channel: [
                    { contact_channel: '', contact_channel_information: '' },
                ],
                customer_lead_type: 'UNCLASSIFIED',
                customer_dob: null,
            },
            company: {
                company_id: 0,
                company_industry: '',
                company_website: '',
            },
            explaination: {
                explaination_provider: '',
                explaination_budget: 0,
                explaination_influencer: '',
                explaination_problem: '',
                explaination_expected: '',
                explaination_result: '',
                explaination_expected_date: null,
            },
        }),
        [customerNameFromBusiness, employee?.employee_id]
    )

    // Form setup
    const methods = useForm<CustomerCreateFormValues>({
        defaultValues,
        resolver: yupResolver(createNewCustomerSchema) as any,
        reValidateMode: 'onChange',
    })

    const { clearOnly } = useFormPersistence(
        methods,
        'customer-create-newv2',
        defaultValues
    )

    const { control, setValue, getValues, trigger } = methods

    // Watch form values
    const customerType = useWatch({ control, name: 'customer.customer_type' })
    const emailValue = useWatch({ control, name: 'customer.customer_email' })
    const phoneValue = useWatch({ control, name: 'customer.customer_phone' })
    const customerSource = useWatch({
        control,
        name: 'customer.customer_source',
    })

    // Handler for creating new company
    const handleNoOptionCompany = useCallback(
        (searchText?: string) => {
            // If search text is purely numeric → pre-fill tax code, else → company name
            const isNumeric = searchText && /^\d+$/.test(searchText.trim())
            setModal<CreateCompanyProps>('CompanyModals/CreateCompany/index', {
                input: isNumeric ? undefined : searchText,
                inputTaxCode: isNumeric ? searchText : undefined,
                onSuccessCreateCompany: (data) => {
                    const company = data?.result
                    // Set as option object so FilterSelectAsync displays label
                    setValue('company.company_id', {
                        label: `${company?.company_tax} - ${company?.company_name}`,
                        value: company?.company_id,
                    } as any)
                    // Auto-fill customer fields from new company
                    if (company?.company_industry)
                        setValue(
                            'customer.customer_industry',
                            company.company_industry
                        )
                    if (company?.company_website)
                        setValue(
                            'customer.customer_website',
                            company.company_website
                        )
                    refetchCompany()
                    closeModal()
                },
            })
        },
        [setModal, setValue, refetchCompany, closeModal]
    )

    // Auto-fill customer fields when company is selected
    const handleCompanySelected = useCallback(
        (option: any) => {
            if (!option) return
            if (option.company_industry)
                setValue('customer.customer_industry', option.company_industry)
            if (option.company_website)
                setValue('customer.customer_website', option.company_website)
        },
        [setValue]
    )

    // Demo tour integration
    const { startDemoTour } = useCustomerCreateTour({ methods, setCurrentStep })
    const { registerHandler, unregisterHandler } = useTourHandler()

    useEffect(() => {
        registerHandler(startDemoTour)
        return () => unregisterHandler()
    }, [registerHandler, unregisterHandler, startDemoTour])

    // Email validation with debounce
    useEffect(() => {
        const checkEmail = async () => {
            setEmailValid(false)
            setEmailExistsResult(null)
            if (!emailValue || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue))
                return

            setEmailChecking(true)
            try {
                const result = await checkCustomerEmailExists(emailValue)
                setEmailValid(!result?.exists)
                if (result?.exists) setEmailExistsResult(result)
            } catch {
                setEmailValid(true)
            } finally {
                setEmailChecking(false)
            }
        }

        const timeout = setTimeout(checkEmail, 500)
        return () => clearTimeout(timeout)
    }, [emailValue])

    // Phone duplicate check with debounce — chỉ báo/chặn khi trùng Lead Marketing
    useEffect(() => {
        const checkPhone = async () => {
            setPhoneMatchesLead(false)
            setPhoneExistsResult(null)
            const phone = (phoneValue || '').trim()
            if (phone.length < 8) return

            setPhoneChecking(true)
            try {
                const result = await checkCustomerPhoneExists(phone)
                if (result?.exists_in_contact_sources) {
                    setPhoneMatchesLead(true)
                    setPhoneExistsResult(result)
                }
            } catch {
                // Lỗi check → không chặn (BE vẫn chặn lúc submit nếu trùng)
            } finally {
                setPhoneChecking(false)
            }
        }

        const timeout = setTimeout(checkPhone, 500)
        return () => clearTimeout(timeout)
    }, [phoneValue])

    // Submit mutation
    const { mutateAsync, isPending } = useMutationWithHandler(
        { mutationFn: createCustomer },
        {
            reactQueryKeys: QUERY_KEYS.CUSTOMERS,
            successMessage: 'Tạo khách hàng mới thành công',
            onSuccess: (response) => {
                clearOnly()
                refetchCurrent()
                const customerId = response.data.result.customer.customer_id
                if (customerNameFromBusiness && customerId) {
                    navigate(
                        `/account/businesses/new?customerId=${encodeURIComponent(customerId.toString())}`
                    )
                } else {
                    navigate(paths.customersList)
                }
            },
        }
    )

    // Step navigation handler
    const handleNext = useCallback(async () => {
        if (currentStep === 1 && (!emailValue || !emailValid)) return

        // Chặn qua bước khi SĐT trùng Lead Marketing (bước 3 nhập SĐT)
        if (currentStep === 3 && phoneMatchesLead) return

        if (currentStep === 4) {
            const values = getValues()
            // Extract company_id from FilterSelectAsync option object
            const companyId =
                typeof values.company?.company_id === 'object'
                    ? (values.company.company_id as any)?.value
                    : values.company?.company_id
            return mutateAsync({
                ...values,
                company: {
                    ...values.company,
                    company_id: companyId || 0,
                    company_industry: values.customer?.customer_industry,
                    company_website: values.customer?.customer_website || '',
                },
            })
        }

        const validations: Record<number, string[]> = {
            1: ['customer.customer_email'],
            2:
                customerType === 'company'
                    ? [
                          'customer.customer_industry',
                          'customer.customer_service_interestings',
                          'customer.customer_sale_followers',
                          'company.company_id',
                      ]
                    : [
                          'customer.customer_industry',
                          'customer.customer_service_interestings',
                          'customer.customer_sale_followers',
                      ],
            3: [
                'customer.customer_name',
                'customer.customer_email',
                'customer.customer_position',
                'customer.customer_phone',
            ],
        }

        const fields = validations[currentStep]
        if (fields) {
            const results = await Promise.all(
                fields.map((f) => trigger(f as any))
            )
            if (results.every(Boolean)) setCurrentStep(currentStep + 1)
        }
    }, [
        currentStep,
        emailValue,
        emailValid,
        phoneMatchesLead,
        customerType,
        getValues,
        mutateAsync,
        trigger,
    ])

    const handleBack = useCallback(() => {
        if (currentStep > 1) setCurrentStep(currentStep - 1)
    }, [currentStep])

    // Quick scan: capture image → API → auto-fill form
    const handleQuickScan = useCallback(
        async (file: File) => {
            setIsScanning(true)
            try {
                const res = await scanBusinessCards([file])
                const result: CardScanResponse = res.result
                const card = result.results[0]
                if (card?.error || !card?.data) {
                    toast.error(card?.error || t('scan_card.error'))
                    return
                }
                const d = card.data
                // Auto-fill customer fields
                if (d.customer_name)
                    setValue('customer.customer_name', d.customer_name)
                if (d.customer_email)
                    setValue('customer.customer_email', d.customer_email)
                if (d.customer_phone)
                    setValue('customer.customer_phone', d.customer_phone)
                if (d.customer_position)
                    setValue('customer.customer_position', d.customer_position)
                if (d.customer_industry)
                    setValue('customer.customer_industry', d.customer_industry)
                if (d.customer_website)
                    setValue('customer.customer_website', d.customer_website)
                if (d.customer_note)
                    setValue('customer.customer_note', d.customer_note)

                // Auto-fill company: search by tax code or name
                const companyQuery = d.company_tax || d.company_name
                if (companyQuery) {
                    const options = await getCompaniesOptions(companyQuery)
                    if (options.length > 0) {
                        // Found existing company → select it
                        setValue('company.company_id', options[0] as any)
                        handleCompanySelected(options[0])
                        setValue('customer.customer_type', 'company')
                    } else if (d.company_website) {
                        // No company found → fill website
                        setValue('company.company_website', d.company_website)
                    }
                }

                toast.success(t('scan_card.autofill_success'))
            } catch (err: any) {
                toast.error(
                    err?.response?.data?.message || t('scan_card.error')
                )
            } finally {
                setIsScanning(false)
                // Reset input so same file can be re-selected
                if (scanInputRef.current) scanInputRef.current.value = ''
            }
        },
        [setValue, t, handleCompanySelected]
    )

    return {
        // Form
        methods,
        control,
        setValue,

        // Step navigation
        currentStep,
        setCurrentStep,
        handleNext,
        handleBack,

        // Email validation
        emailChecking,
        emailValid,
        emailValue,
        emailExistsResult,

        // Phone validation (realtime — chỉ chặn khi trùng Lead Marketing)
        phoneChecking,
        phoneMatchesLead,
        phoneValue,
        phoneExistsResult,

        // Form values
        customerType,
        customerSource,

        // Handlers
        handleNoOptionCompany,
        handleCompanySelected,

        // Quick scan
        isScanning,
        scanInputRef,
        handleQuickScan,

        // State
        isPending,

        // Translation
        t,
    }
}
